import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import MainHero from '@/components/MainHero'
import ParallaxText from '@/components/ParallaxText'
import DiagonalGallery from '@/components/DiagonalGallery'
import BtnLink from '@/components/BtnLink'
import RelatedProducts from '@/components/RelatedProducts'
import Meta from '@/components/Meta'
import Footer from '@/components/Footer'
import { externalPreload } from '@/utils/dom'
import actions from '@/actions'
import utils from '@/utils'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

const useStyles = createUseStyles(style)

const People = ({
  cpt: cptFromProps,
}) => {
  const classes = useStyles()
  const { getImageUrl, setBackButtonVisible, setBackButtonLink } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const preload = useRef(new CreateJs.LoadQueue())
  const { stub } = useParams()
  const [load, setLoad] = useState(false)
  const gallery = useRef([])
  const $root = useRef()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, strings, collections } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub] || {},
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
    collections: state.page.taxonomies._cta_collection || [], // eslint-disable-line no-underscore-dangle
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getSinglePeople = useCallback((cpt, index) => dispatch(actions.page.getDataCPT(cpt, index)), [dispatch])
  const getTaxonomiesList = useCallback((data) => dispatch(actions.page.getAllTerms(data)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub, 'pages')
    preload.current.loadManifest(heroMedia)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = () => {
    if (page.project_gallery_id) {
      const gal = page.project_gallery_id.split(',')
      const imgSize = mediaQueryKey > 2 ? 'full' : 'large'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      gallery.current = galleryItems[0] ? galleryItems : []
    }
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
      setGallery()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    if (!page) getSinglePeople(cptFromProps, stub)
    if (!collections.length) getTaxonomiesList('_cta_collection')
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Setting del Back Button
  ------------------------------*/
  useEffect(() => {
    if (load) {
      setBackButtonVisible(true)
      setBackButtonLink(strings.archive_people_menu_link)
    }
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover People
  ------------------------------*/
  const renderCoverProject = () => {
    return (page.title.rendered && page.featured_media) && (
      <MainHero
        className={classes.hero}
        title={page.title.rendered.replace('-', '- ').split(' ')}
        img={getImageUrl(page.featured_media, 'full', stub, 'pages')}
        ready={ready}
        scrollIcon
        parallax
        delay={1}
        bigFont
      />
    )
  }

  /*------------------------------
  Render Intro People
  ------------------------------*/
  const renderIntroPeople = () => {
    return page.content.rendered && (
      <div
        className={`${classes.wrapper} ${classes.introText}`}
        dangerouslySetInnerHTML={{ __html: page.content.rendered }}
      />
    )
  }

  /*------------------------------
  Render Diagonal Gallery
  ------------------------------*/
  const renderDiagonalGallery = () => {
    return page.project_gallery_id && (
      <div className={classes.wrapper}>
        <DiagonalGallery
          items={gallery.current}
        />
      </div>
    )
  }

  /*------------------------------
  Render Parallax Text
  ------------------------------*/
  const renderParallaxText = () => {
    const img1 = (page.parallax_img_1 !== '') ? getImageUrl(page.parallax_img_1, 'large', stub) : null
    const img2 = (page.parallax_img_2 !== '') ? getImageUrl(page.parallax_img_2, 'large', stub) : null
    const img3 = (page.parallax_img_3 !== '') ? getImageUrl(page.parallax_img_3, 'large', stub) : null
    const img4 = (page.parallax_img_4 !== '') ? getImageUrl(page.parallax_img_4, 'large', stub) : null

    return page.parallax_text && (
      <ParallaxText
        className={classes.parallaxText}
        data={{
          text: page.parallax_text,
          img1,
          img2,
          img3,
          img4,
        }}
      />
    )
  }

  /*------------------------------
  Render Words
  ------------------------------*/
  const renderWords = () => {
    return page.banner_single_text && (
      <div className={`${classes.wrapper} ${classes.words}`}>
        <div className={classes.grid}>
          <div className={classes.gridHeading}>
            <h2>{strings.words_title}</h2>
          </div>
          <div className={classes.gridContent}>
            {page.banner_single_text && <p dangerouslySetInnerHTML={{ __html: decodeEntities(page.banner_single_text) }} />}
            {page.banner_single_link && (
              <BtnLink
                link={page.banner_single_link}
                label={page.banner_single_label}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Related Collections
  ------------------------------*/
  const renderRelatedCollections = () => {
    return (page._cta_collection.length > 0 && collections.length > 0) && ( // eslint-disable-line no-underscore-dangle
      <>
        <div className={classes.wrapper}>
          <div className={`${classes.grid} ${classes.gridColumn}`}>
            <div className={classes.gridHeading}>
              <h2 dangerouslySetInnerHTML={{ __html: `${utils.strings.insertBr(page.title.rendered)}<br />${strings.for_rometti_title}` }} />
            </div>
          </div>
        </div>
        <RelatedProducts
          items={collections.filter((c) => page._cta_collection.includes(c.id)).map((item) => ({ // eslint-disable-line no-underscore-dangle
            id: item.id,
            link: item.link,
            title: item.name,
            image: item.meta.taxonomy_listing_image_url,
          }))}
          title={strings.collections_menu_label}
          label={strings.discover}
        />
      </>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverProject()}
        {renderIntroPeople()}
        {renderDiagonalGallery()}
        {renderParallaxText()}
        {renderWords()}
        {renderRelatedCollections()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pagePeople ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default People
